import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { ApiKeyService } from '../services/api-key.service';
import { NotificationService } from '@core/services/notification.service';
import { httpErrorMessage } from '@core/utils/http-error';
import * as ApiKeysActions from './api-keys.actions';

/**
 * API Keys Effects
 * Handles side effects for API key actions
 */
@Injectable()
export class ApiKeysEffects {
  private actions$ = inject(Actions);
  private apiKeyService = inject(ApiKeyService);
  private notificationService = inject(NotificationService);

  /**
   * Load API keys list effect
   */
  loadApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.loadApiKeys),
      switchMap(({ filters }) =>
        this.apiKeyService.getApiKeys(filters).pipe(
          map(data => ApiKeysActions.loadApiKeysSuccess({ data })),
          catchError(error => {
            console.error('Error loading API keys:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to load API keys';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.loadApiKeysFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Load single API key effect
   */
  loadApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.loadApiKey),
      switchMap(({ keyId }) =>
        this.apiKeyService.getApiKey(keyId).pipe(
          map(apiKey => ApiKeysActions.loadApiKeySuccess({ apiKey })),
          catchError(error => {
            console.error('Error loading API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to load API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.loadApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Create API key effect
   */
  createApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.createApiKey),
      switchMap(({ request }) =>
        this.apiKeyService.createApiKey(request).pipe(
          map(response => {
            this.notificationService.showSuccess('API key created successfully!');
            return ApiKeysActions.createApiKeySuccess({ response });
          }),
          catchError(error => {
            console.error('Error creating API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to create API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.createApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Update API key effect
   */
  updateApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.updateApiKey),
      switchMap(({ keyId, request }) =>
        this.apiKeyService.updateApiKey(keyId, request).pipe(
          map(apiKey => {
            this.notificationService.showSuccess('API key updated successfully');
            return ApiKeysActions.updateApiKeySuccess({ apiKey });
          }),
          catchError(error => {
            console.error('Error updating API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to update API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.updateApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Revoke API key effect
   */
  revokeApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.revokeApiKey),
      switchMap(({ keyId, request }) =>
        this.apiKeyService.revokeApiKey(keyId, request).pipe(
          map(() => {
            this.notificationService.showSuccess('API key revoked successfully');
            return ApiKeysActions.revokeApiKeySuccess({ keyId });
          }),
          catchError(error => {
            console.error('Error revoking API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to revoke API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.revokeApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Delete API key effect (permanent delete)
   */
  deleteApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.deleteApiKey),
      switchMap(({ keyId }) =>
        this.apiKeyService.deleteApiKey(keyId).pipe(
          map(() => {
            this.notificationService.showSuccess('API key deleted permanently');
            return ApiKeysActions.deleteApiKeySuccess({ keyId });
          }),
          catchError(error => {
            console.error('Error deleting API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to delete API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.deleteApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Regenerate API key effect
   */
  regenerateApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.regenerateApiKey),
      switchMap(({ keyId }) =>
        this.apiKeyService.regenerateApiKey(keyId).pipe(
          map(response => {
            this.notificationService.showSuccess('API key regenerated successfully!');
            return ApiKeysActions.regenerateApiKeySuccess({ response });
          }),
          catchError(error => {
            console.error('Error regenerating API key:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to regenerate API key';
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.regenerateApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Rotate API key effect
   */
  rotateApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.rotateApiKey),
      switchMap(({ keyId, request }) =>
        this.apiKeyService.rotateApiKey(keyId, request).pipe(
          map(response => {
            this.notificationService.showSuccess('API key rotated successfully! New key generated.');
            return ApiKeysActions.rotateApiKeySuccess({ response });
          }),
          catchError(error => {
            console.error('Error rotating API key:', error);
            const errorMessage = httpErrorMessage(error, 'Failed to rotate API key');
            this.notificationService.showError(errorMessage);
            return of(ApiKeysActions.rotateApiKeyFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Load API key usage stats effect
   */
  loadApiKeyUsageStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeysActions.loadApiKeyUsageStats),
      switchMap(({ keyId }) =>
        this.apiKeyService.getApiKeyUsageStats(keyId).pipe(
          map(stats => ApiKeysActions.loadApiKeyUsageStatsSuccess({ stats })),
          catchError(error => {
            console.error('Error loading usage stats:', error);
            const errorMessage = error.error?.detail || error.message || 'Failed to load usage statistics';
            return of(ApiKeysActions.loadApiKeyUsageStatsFailure({ error: errorMessage }));
          })
        )
      )
    )
  );
}

